// RevenueChart.tsx
import { CartesianGrid, ComposedChart, XAxis, YAxis } from 'recharts';
import type { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegendContentV2
} from '@/components/ui/chart';

import { CHART_CONFIG, CHART_GRID_COLOR, CHART_TICK_COLOR } from '../config/chart-config';
import { MetricsDataPoint } from './revenue-metrics-chart';
import { TabType } from '@/routes/dashboards/shared-types';
import { tickFormatter } from './revenue-chart-tick-formatter';
import { getMetricsForTab } from './get-metrics-for-tab';
import { renderMetric } from './render-metric';
import { useEmqScore } from '../hooks/emqScoreHook';

export interface RevenueChartProps {
  data: MetricsDataPoint[];
  initialTab: TabType;
  yDomain?: number[];
  yTicks?: number[];
  marginConfig?: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  tooltipContentFormatter?: (value: ValueType, name: NameType, props: any) => React.ReactNode;
  containerHeight?: number;
}

const DEFAULT_MARGIN_CONFIG = {
  top: 20,
  right: 20,
  bottom: 20,
  left: 10
};

export const RevenueChart = ({
  data,
  initialTab,
  yDomain,
  yTicks,
  marginConfig = DEFAULT_MARGIN_CONFIG,
  tooltipContentFormatter,
  containerHeight = 388
}: RevenueChartProps) => {
  const isEMQScoreDashboardEnabled = useEmqScore();
  return (
    <ChartContainer config={CHART_CONFIG} containerHeight={containerHeight} className="w-full p-0">
      <ComposedChart data={data} margin={marginConfig} barGap={10}>
        <CartesianGrid stroke={CHART_GRID_COLOR} vertical={false} />
        <XAxis
          dataKey="date"
          axisLine={false}
          color={CHART_TICK_COLOR}
          tickLine={false}
          tickMargin={8}
        />
        <YAxis
          tickFormatter={(value: number) => tickFormatter(value, initialTab)}
          color={CHART_TICK_COLOR}
          axisLine={false}
          tickLine={false}
          tickMargin={8}
          ticks={yTicks}
          domain={yDomain}
        />

        <ChartTooltip content={<ChartTooltipContent formatter={tooltipContentFormatter} />} />
        {isEMQScoreDashboardEnabled ? (
          <ChartLegend content={<ChartLegendContentV2 />} />
        ) : (
          <ChartLegend content={<ChartLegendContent />} />
        )}
        {getMetricsForTab(initialTab, data).map((metric) => renderMetric(metric, initialTab, data))}
      </ComposedChart>
    </ChartContainer>
  );
};
